import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

interface DeletionConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  accountType?: string;
}

export const DeletionConfirmationModal: React.FC<DeletionConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  accountType = 'アカウント',
}) => {
  const finalConfirmations = [
    'この操作は取り消すことができません',
    'すべてのデータは保持されますが、ログインはできなくなります',
    '管理者が必要に応じてデータを確認・管理します',
    '退会後は新規登録が必要です'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ mr: 2, color: 'error.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              最終確認
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {accountType}の退会を実行しますか？
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            本当に{accountType}を退会しますか？
          </Typography>
          <Typography variant="body2">
            この操作は即座に実行され、元に戻すことはできません。
          </Typography>
        </Alert>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            退会前にご確認ください：
          </Typography>
          <List dense>
            {finalConfirmations.map((confirmation, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <CheckIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText 
                  primary={confirmation}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            退会を実行する場合は、下の「退会を実行する」ボタンをクリックしてください。
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
          variant="outlined"
          size="large"
          sx={{ mr: 2 }}
        >
          キャンセル
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          color="error"
          size="large"
          startIcon={isLoading ? undefined : <DeleteIcon />}
          sx={{ 
            px: 3,
            fontWeight: 'bold'
          }}
        >
          {isLoading ? '処理中...' : '退会を実行する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
